import { watch } from 'vue';
import { useRoute } from 'vue-router';
import { useIonRouter } from '@ionic/vue';
import { useUser } from '@/api/user/queries/use-user';
import { RouteName } from '@/router/types';
import { setMetaAccount } from '@/services/meta-app-events';
import { setSessionReplayAccount } from '@/services/session-replay';

// Screens a gated account may stay on.
const GATE_PATHS = new Set([
  `/${RouteName.CompleteRegistration}`,
  `/${RouteName.AgeRestricted}`,
  `/${RouteName.PrivacyPolicy}`,
  `/${RouteName.Login}`,
]);

/**
 * Age rules for the signed-in account (backend ageStatus from GET /api/user):
 * - `missing`: the date of birth is asked once (an account with a valid one is never asked);
 * - `under_minimum`: the restricted screen, no normal app access.
 * An older backend sends no ageStatus, which changes nothing.
 *
 * Meta ad measurement and Session Replay eligibility (18+) follow every date-of-birth change,
 * not only the first load: under 18 stops both at once.
 */
export function useAgeGate() {
  const { data: user } = useUser();
  const route = useRoute();
  const ionRouter = useIonRouter();

  watch(
    () => [user.value?.ageStatus, route.path] as const,
    ([status, path]) => {
      if (GATE_PATHS.has(path)) return;
      if (status === 'missing') ionRouter.replace(`/${RouteName.CompleteRegistration}?mode=profile`);
      else if (status === 'under_minimum') ionRouter.replace(`/${RouteName.AgeRestricted}`);
    },
    { immediate: true }
  );

  watch(
    () => (user.value ? `${user.value.id}|${user.value.profile?.dateOfBirth ?? ''}` : null),
    key => {
      if (!key || !user.value) return;
      void setMetaAccount(user.value);
      setSessionReplayAccount(user.value);
    }
  );
}
